import { ButtonPrimary, ButtonSecondary, IconButton } from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { FileExtension, SIGNAL_QUALITY_LABELS, SupportedRegions } from "@nova/constants"
import { isLocalhostEnvironment } from "@nova/configuration/browser"
import {
    CreateManualMissionBody,
    CustomFieldAnswer,
    CustomFieldSpec,
    DroneManufacturer,
    IngestDataType,
    MissionProvider,
} from "@nova/schemas"
import { triggerMockIngest } from "@nova/util-ingest"
import { createFileFingerprint, pluralize } from "@nova/util-strings"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectUserID } from "user/profile/selectors"
import { UploadDropBox } from "panels/content/side-panel/upload-flow/components/UploadDropBox"
import { FunctionComponent, useState } from "react"
import * as Icons from "@nova/component-icons"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { setGlobalNotificationContext } from "user/profile/slice"
import { usePrepareCsvLogbookUpload, usePrepareFlightRecordUpload } from "./FlightRecordQueries"
import { useCreateManualMission } from "./PilotTrackingMissionQueries"
import { parseFlightPathFile } from "./parseFlightPath"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { TabHeader } from "themes/default-tailwind/buttons/TabHeader"
import { InputOutlined, TextAreaOutlined } from "@nova/ds-component-input"
import { PilotPicker } from "./PilotPicker"
import { AircraftPicker } from "./AircraftPicker"
import { Divider } from "components/Divider"
import { FlightPathMapPreview, LatLng } from "./FlightPathMapPreview"
import { ExpandableContent } from "@nova/component-expandable-content"
import { SelectOutlined } from "@nova/component-select"
import { useCustomFieldDefinition } from "../missions/linked-layers/CustomFieldsQueries"

const DJI_FILENAME_REGEX =
    /^(?:(?:DJI)?FlightRecord_)?\d{4}-\d{2}-\d{2}_\[\d{2}-\d{2}-\d{2}\]\.txt$/
const AUTEL_FILENAME_REGEX = /^autel[^.]*(?:\.(bin|zip))?$/i
const CSV_FILENAME_REGEX = /\.csv$/i

const FileRow: FunctionComponent<{
    file: File
    onRemove: () => void
    disabled?: boolean
}> = ({ file, onRemove, disabled }) => (
    <div className="flex items-center justify-between gap-5">
        {file.name}
        <IconButton onClick={onRemove} className="hover:!text-red-500" disabled={disabled}>
            <Icons.SmallIcon Icon={Icons.Trash} />
        </IconButton>
    </div>
)

const CSV_UPLOAD_KIND = "csv-logbook"
type UploadKind = DroneManufacturer | typeof CSV_UPLOAD_KIND

function detectUploadKind(file: File): UploadKind | null {
    if (CSV_FILENAME_REGEX.test(file.name)) return CSV_UPLOAD_KIND
    if (DJI_FILENAME_REGEX.test(file.name)) return DroneManufacturer.DJI
    if (AUTEL_FILENAME_REGEX.test(file.name)) return DroneManufacturer.Autel
    return null
}

const CreateFlightsImport: FunctionComponent<{
    onConfirm: () => void
    onCancel: () => void
}> = ({ onConfirm, onCancel }) => {
    const [files, setFiles] = useState<File[]>([])
    const [pilotUserId, setPilotUserId] = useState("")
    const [isUploading, setIsUploading] = useState(false)

    const dispatch = useAppDispatch()
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const userId = useAppSelector(selectUserID)
    const { prepareUpload, isPreparing } = usePrepareFlightRecordUpload()
    const { prepareCsvUpload, isPreparingCsv } = usePrepareCsvLogbookUpload()

    const detectedKind = files.length > 0 ? detectUploadKind(files[0]) : null
    const isCsvKind = detectedKind === CSV_UPLOAD_KIND
    const isLoading = isPreparing || isPreparingCsv || isUploading
    const canSubmit =
        files.length > 0 && !!detectedKind && (isCsvKind || !!pilotUserId) && !isLoading

    const handleFileDeleteClick = (i: number) => {
        setFiles((prev) => {
            const copy = [...prev]
            copy.splice(i, 1)
            return copy
        })
    }

    const handleNewFiles = async (items: File[]) => {
        const existingKind = files.length > 0 ? detectUploadKind(files[0]) : null
        const acceptedFiles: File[] = []
        let batchKind = existingKind
        const invalidNames: string[] = []
        const mixedNames: string[] = []

        items.forEach((file) => {
            const kind = detectUploadKind(file)
            if (!kind) invalidNames.push(file.name)
            else if (batchKind !== null && kind !== batchKind) mixedNames.push(file.name)
            else {
                batchKind = kind
                acceptedFiles.push(file)
            }
        })

        if (invalidNames.length > 0)
            dispatch(
                setGlobalNotificationContext({
                    message: "Unrecognized flight log type.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 6000,
                })
            )
        if (mixedNames.length > 0)
            dispatch(
                setGlobalNotificationContext({
                    message:
                        "Cannot mix logbook CSVs with drone flight logs, or logs from different manufacturers.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 6000,
                })
            )

        if (acceptedFiles.length > 0) {
            setFiles((prev) => [...prev, ...acceptedFiles])
        }
    }

    const handleSubmit = async () => {
        if (!detectedKind) return

        const isCsv = detectedKind === CSV_UPLOAD_KIND

        let signedUrlByFileId: Record<string, string | undefined>
        try {
            if (isCsv) {
                const prepared = await Promise.all(
                    files.map(async (file) => {
                        const result = await prepareCsvUpload({
                            fileName: file.name,
                            fingerprint: createFileFingerprint(file.name, file.size),
                        })
                        return result ? [result.fileId, result.url] : null
                    })
                )
                signedUrlByFileId = Object.fromEntries(
                    prepared.filter(Boolean) as [string, string][]
                )
            } else {
                signedUrlByFileId =
                    (await prepareUpload({
                        files: files.map((file) => ({
                            fileName: file.name,
                            fingerprint: createFileFingerprint(file.name, file.size),
                        })),
                        manufacturer: detectedKind,
                        provider: MissionProvider.NovaWebApp,
                        pilotUserId,
                    })) ?? {}
            }
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to prepare upload. Please try again.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 6000,
                })
            )
            return
        }

        if (!signedUrlByFileId) return

        setIsUploading(true)
        try {
            const results = await Promise.allSettled(
                files.map((file) => {
                    const fingerprint = createFileFingerprint(file.name, file.size)
                    const uploadUrl = signedUrlByFileId[fingerprint]
                    if (!uploadUrl) return Promise.resolve()
                    return fetch(uploadUrl, { method: "PUT", body: file }).then((res) => {
                        if (!res.ok) throw new Error(file.name)
                        if (!isLocalhostEnvironment() || !userId) return
                        const s3Key = new URL(uploadUrl).pathname.slice(1)
                        triggerMockIngest({
                            url: "/api-gateway/mock-ingest-server",
                            credentials: "include",
                            headers: { "Content-Type": "application/json" },
                            body: {
                                userId,
                                s3Key,
                                payload: isCsv
                                    ? {
                                          orgId: orgId!,
                                          dataType: IngestDataType.CsvLogbook,
                                          fileId: fingerprint,
                                      }
                                    : {
                                          orgId: orgId!,
                                          dataType: IngestDataType.FlightRecord,
                                          fileId: fingerprint,
                                          provider: MissionProvider.NovaWebApp,
                                          pilotUserId,
                                      },
                                region: SupportedRegions.Canada,
                                filename: file.name,
                                requestId: `mock-ingest-event-${Date.now()}`,
                                size: file.size,
                            },
                        })
                    })
                })
            )

            const failed = results
                .map((r, i) => (r.status === "rejected" ? files[i].name : null))
                .filter(Boolean)

            if (failed.length > 0) {
                dispatch(
                    setGlobalNotificationContext({
                        message: `Failed to upload: ${failed.join(", ")}`,
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 6000,
                    })
                )
            } else {
                dispatch(
                    setGlobalNotificationContext({
                        message: `${pluralize({ count: files.length, str: "file" })} uploaded. Processing may take a few minutes.`,
                        status: GlobalNotificationStatus.success,
                        timeoutInMs: 5000,
                    })
                )
                onConfirm()
            }
        } finally {
            setIsUploading(false)
        }
    }

    return (
        <div>
            <h4 className="text-gray-300 pb-4">Upload Autel or DJI flight log files.</h4>
            <div className="w-full flex flex-col gap-2">
                <UploadDropBox
                    allowedFileTypes={[FileExtension.all]}
                    isProcessing={isLoading}
                    onNewItems={handleNewFiles}
                    supportedMimeTypes={[]}
                    title="Upload Files"
                    canAcceptMultiple
                />
                <ScrollableArea className="flex-1">
                    {files.map((file, i) => (
                        <FileRow
                            key={file.name + `${i}`}
                            file={file}
                            onRemove={() => handleFileDeleteClick(i)}
                            disabled={isLoading}
                        />
                    ))}
                </ScrollableArea>
            </div>
            {!isCsvKind && (
                <PilotPicker label="Assign Pilot" value={pilotUserId} onChange={setPilotUserId} />
            )}
            <div className="sticky bottom-0 flex items-center gap-3 justify-end w-full mt-9 pt-3 bg-gray-900">
                <ButtonSecondary title="Cancel" onClick={onCancel} disabled={isLoading} />
                <ButtonPrimary
                    title="Create Flight"
                    onClick={() => void handleSubmit()}
                    disabled={!canSubmit}
                    isLoading={isLoading}
                />
            </div>
        </div>
    )
}

const ManualCustomFieldInput: FunctionComponent<{
    field: CustomFieldSpec
    value: CustomFieldAnswer["value"] | undefined
    onChange: (value: CustomFieldAnswer["value"]) => void
}> = ({ field, value, onChange }) => {
    switch (field.type) {
        case "text":
            return (
                <InputOutlined
                    label={field.label}
                    value={typeof value === "string" ? value : ""}
                    onChange={(e) => onChange(e.currentTarget.value)}
                />
            )
        case "yes-no":
            return (
                <div className="flex flex-col gap-2 w-full">
                    <label className="text-gray-100">{field.label}</label>
                    <SelectOutlined<boolean | undefined>
                        value={typeof value === "boolean" ? value : undefined}
                        options={[
                            { label: "Yes", value: true },
                            { label: "No", value: false },
                        ]}
                        onChange={(v) => v !== undefined && onChange(v)}
                        placeholder="Select"
                    />
                </div>
            )
        case "multiple-choice":
            return (
                <div className="flex flex-col gap-2 w-full">
                    <label className="text-gray-100">{field.label}</label>
                    <SelectOutlined<string | undefined>
                        value={typeof value === "string" ? value : undefined}
                        options={field.options.map((o) => ({ label: o, value: o }))}
                        onChange={(v) => v !== undefined && onChange(v)}
                        placeholder="Select"
                    />
                </div>
            )
    }
}

const ManualCustomFields: FunctionComponent<{
    fields: CustomFieldSpec[]
    answersById: Map<string, CustomFieldAnswer["value"]>
    onChange: (id: string, value: CustomFieldAnswer["value"]) => void
}> = ({ fields, answersById, onChange }) => {
    if (fields.length === 0) return null

    return (
        <>
            <Divider />
            <div className="flex flex-col gap-4">
                <h4 className="text-white">Custom Fields</h4>
                {fields.map((field) => (
                    <ManualCustomFieldInput
                        key={field.id}
                        field={field}
                        value={answersById.get(field.id)}
                        onChange={(value) => onChange(field.id, value)}
                    />
                ))}
            </div>
        </>
    )
}

const CreateFlightsManual: FunctionComponent<{
    onConfirm: () => void
    onCancel: () => void
}> = ({ onConfirm, onCancel }) => {
    const [aircraftId, setAircraftId] = useState("")
    const [pilotUserId, setPilotUserId] = useState("")
    const [flightPathFile, setFlightPathFile] = useState<File | null>(null)
    const [takeoffCoords, setTakeoffCoords] = useState<LatLng | null>(null)
    const [coordsInput, setCoordsInput] = useState("")
    const [isExtraFieldsOpen, setIsExtraFieldsOpen] = useState(false)
    const [startedAt, setStartedAt] = useState("")
    const [endedAt, setEndedAt] = useState("")
    const [takeoffBattery, setTakeoffBattery] = useState<number>()
    const [landingBattery, setLandingBattery] = useState<number>()
    const [signalQuality, setSignalQuality] = useState<number | null>(null)
    const [notes, setNotes] = useState("")
    const [customAnswers, setCustomAnswers] = useState<Map<string, CustomFieldAnswer["value"]>>(
        new Map()
    )

    const dispatch = useAppDispatch()
    const { createMission, isCreating } = useCreateManualMission()
    const { data: customFieldDefinition } = useCustomFieldDefinition()
    const customFields = customFieldDefinition?.form ?? []

    const setCustomAnswer = (id: string, value: CustomFieldAnswer["value"]) =>
        setCustomAnswers((prev) => new Map(prev).set(id, value))

    const endBeforeStart = !!endedAt && !!startedAt && new Date(endedAt) <= new Date(startedAt)
    const hasLocation = !!flightPathFile || !!takeoffCoords
    const canSubmit =
        !!aircraftId &&
        !!pilotUserId &&
        !!startedAt &&
        hasLocation &&
        !endBeforeStart &&
        !isCreating

    const handleSubmit = async () => {
        if (!canSubmit) return

        let flightPath: [number, number][] | undefined
        if (flightPathFile) {
            try {
                const points = await parseFlightPathFile(flightPathFile)
                if (points.length > 0) flightPath = points
            } catch {
                dispatch(
                    setGlobalNotificationContext({
                        message: "Failed to parse flight path.",
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 6000,
                    })
                )
                return
            }
        }

        const customFieldAnswers: CustomFieldAnswer[] = customFields
            .filter((field) => {
                const value = customAnswers.get(field.id)
                return value !== undefined && value !== ""
            })
            .map((field) => ({ id: field.id, value: customAnswers.get(field.id)! }))

        const body: CreateManualMissionBody = {
            aircraftId,
            userId: pilotUserId,
            startedAt: new Date(startedAt).toISOString(),
            endedAt: endedAt ? new Date(endedAt).toISOString() : undefined,
            takeoffLatitude: takeoffCoords?.lat,
            takeoffLongitude: takeoffCoords?.lng,
            notes: notes || undefined,
            flightPath,
            signalQuality: signalQuality ?? undefined,
            takeoffBatteryPercent: Number.isNaN(takeoffBattery) ? undefined : takeoffBattery,
            landingBatteryPercent: Number.isNaN(landingBattery) ? undefined : landingBattery,
            customFieldAnswers: customFieldAnswers.length ? customFieldAnswers : undefined,
        }

        try {
            await createMission(body)
            dispatch(
                setGlobalNotificationContext({
                    message: "Flight created.",
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 5000,
                })
            )
            onConfirm()
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to create flight. Please try again.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 6000,
                })
            )
        }
    }

    const handleCoordsInputChange = (raw: string) => {
        setCoordsInput(raw)
        const [latStr, lngStr] = raw.split(",").map((s) => s.trim())
        const lat = parseFloat(latStr)
        const lng = parseFloat(lngStr)
        if (!isNaN(lat) && !isNaN(lng)) setTakeoffCoords({ lat, lng })
    }

    const handleMapMove = (next: LatLng) => {
        const rounded = { lat: Number(next.lat.toFixed(5)), lng: Number(next.lng.toFixed(5)) }
        setTakeoffCoords(rounded)
        setCoordsInput(`${rounded.lat}, ${rounded.lng}`)
    }

    const handleCoordsInputBlur = () => {
        if (!takeoffCoords) return
        setCoordsInput(`${takeoffCoords.lat.toFixed(5)}, ${takeoffCoords.lng.toFixed(5)}`)
    }

    return (
        <div className="flex flex-col gap-6 pl-px">
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-4">
                    <InputOutlined
                        label="Start Date Time *"
                        required
                        type="datetime-local"
                        value={startedAt}
                        onChange={(e) => setStartedAt(e.currentTarget.value)}
                    />
                    <InputOutlined
                        label="End Date Time"
                        type="datetime-local"
                        value={endedAt}
                        min={startedAt || undefined}
                        onChange={(e) => setEndedAt(e.currentTarget.value)}
                    />
                </div>
                {endBeforeStart && (
                    <span className="text-red-500 text-xs">
                        End date time must be after start date time.
                    </span>
                )}
            </div>

            <div className="flex items-center gap-4">
                <PilotPicker label="Pilot *" onChange={setPilotUserId} value={pilotUserId} />
                <AircraftPicker label="Drone *" onChange={setAircraftId} value={aircraftId} />
            </div>

            <ManualCustomFields
                fields={customFields}
                answersById={customAnswers}
                onChange={setCustomAnswer}
            />

            <Divider />

            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <h4 className="text-gray-100">Takeoff Location *</h4>
                    <h5 className="text-gray-100">
                        Upload your flight path, or manually enter your takeoff coordinates
                    </h5>
                    <div className="flex items-center gap-5 pt-2">
                        {!flightPathFile && (
                            <>
                                <label className="flex-1 flex items-center justify-center gap-3 py-1 px-3 border border-dashed border-white-500 rounded-md cursor-pointer hover:border-blue-400 transition-colors">
                                    <input
                                        type="file"
                                        accept=".kml,.gpx"
                                        className="hidden"
                                        onChange={(e) =>
                                            setFlightPathFile(e.currentTarget.files?.[0] ?? null)
                                        }
                                    />
                                    <Icons.Icon Icon={Icons.Backup} className="text-gray-0" />
                                    <span className="text-white">
                                        Upload Flight Path
                                        <span className="text-gray-300"> (KML, GPX)</span>
                                    </span>
                                </label>
                                <InputOutlined
                                    className="flex-1"
                                    placeholder="Latitude, Longitude"
                                    value={coordsInput}
                                    onChange={(e) => handleCoordsInputChange(e.currentTarget.value)}
                                    onBlur={handleCoordsInputBlur}
                                />
                            </>
                        )}

                        {flightPathFile && (
                            <div className="w-fit">
                                <FileRow
                                    file={flightPathFile}
                                    onRemove={() => setFlightPathFile(null)}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="rounded-lg h-[200px]">
                    <FlightPathMapPreview
                        flightPathFile={flightPathFile}
                        takeoffCoords={takeoffCoords}
                        onTakeoffChange={handleMapMove}
                    />
                </div>
                <button
                    type="button"
                    onClick={() => setIsExtraFieldsOpen((o) => !o)}
                    className="flex items-center gap-2 text-gray-100 w-fit"
                >
                    <Icons.SmallIcon
                        Icon={Icons.ChevronRight}
                        className={`transition-transform ${isExtraFieldsOpen ? "rotate-90" : ""}`}
                    />
                    Additional Details
                </button>
                <ExpandableContent isOpen={isExtraFieldsOpen}>
                    <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-4">
                            <InputOutlined
                                label="Takeoff Battery %"
                                type="number"
                                value={takeoffBattery}
                                max={100}
                                min={0}
                                onChange={(e) =>
                                    setTakeoffBattery(parseFloat(e.currentTarget.value))
                                }
                            />
                            <InputOutlined
                                label="Landing Battery %"
                                type="number"
                                value={landingBattery}
                                max={100}
                                min={0}
                                onChange={(e) =>
                                    setLandingBattery(parseFloat(e.currentTarget.value))
                                }
                            />
                        </div>
                        <div className="flex flex-col gap-2 flex-[0.5] pr-4">
                            <label className="text-gray-100">Signal Quality</label>
                            <SelectOutlined<number | null>
                                onChange={setSignalQuality}
                                options={SIGNAL_QUALITY_LABELS.map((label, value) => ({
                                    label,
                                    value,
                                }))}
                                value={signalQuality}
                                placeholder="Select signal quality"
                            />
                        </div>
                        <TextAreaOutlined
                            label="Notes"
                            value={notes}
                            onChange={(e) => setNotes(e.currentTarget.value)}
                        />
                    </div>
                </ExpandableContent>
            </div>
            <div className="sticky bottom-0 flex items-center gap-3 justify-end w-full mt-2 pt-3 bg-gray-900">
                <ButtonSecondary title="Cancel" onClick={onCancel} disabled={isCreating} />
                <ButtonPrimary
                    title="Create Flight"
                    onClick={() => void handleSubmit()}
                    disabled={!canSubmit}
                    isLoading={isCreating}
                />
            </div>
        </div>
    )
}

const CreateFlightsModal: FunctionComponent<{ onConfirm: () => void; onCancel: () => void }> = ({
    onConfirm,
    onCancel,
}) => {
    const [tab, setTab] = useState<"import" | "manual">("import")

    return (
        <HighPriorityNovaModalContainer
            hideTitleUnderline
            closeModal={onCancel}
            title={tab === "import" ? "Create Flights" : "Create a Flight"}
            className="max-h-wallboard-content w-[600px]"
        >
            <div className="flex flex-col gap-4 shrink min-h-0">
                <div className="flex items-center w-full">
                    <TabHeader
                        currentMode={tab}
                        mode="import"
                        setActiveMode={() => setTab("import")}
                        title="Import"
                        className="flex-1"
                    />

                    <TabHeader
                        currentMode={tab}
                        mode="manual"
                        setActiveMode={() => setTab("manual")}
                        title="Manual"
                        className="flex-1"
                    />
                </div>
                <ScrollableArea
                    shouldPreventScrollbarShifting
                    className="flex flex-col gap-4 h-full pr-3"
                >
                    {tab === "import" ? (
                        <CreateFlightsImport onConfirm={onConfirm} onCancel={onCancel} />
                    ) : (
                        <CreateFlightsManual onConfirm={onConfirm} onCancel={onCancel} />
                    )}
                </ScrollableArea>
            </div>
        </HighPriorityNovaModalContainer>
    )
}

export { CreateFlightsModal }
